import React, { FC, ReactElement, forwardRef, useCallback } from 'react';
import { UserType } from 'api/models';
import { Project } from 'api/project';
import { RbacPermissionRule } from 'api/rbac/rbac-types';
import { useUser } from 'hooks/use-user';
import { ProjectResourceOrUndefined, useWithAccess } from './useWithAccess';
import type { UseWithAccess } from './useWithAccess';

export function withAccess<P extends any>(
  WrappedComponent: React.ComponentType<P>,
) {
  const WithAccess = forwardRef<unknown, P & UseWithAccess>(
    (props: P & UseWithAccess, ref) => {
      const { rule, resourceData, propsIfNoAccess, children, actionScope } =
        props;
      const { hasAccessToResource } = useWithAccess();
      const hasAccess = hasAccessToResource({
        rule,
        resourceData,
        actionScope,
      });

      if (!hasAccess) {
        return propsIfNoAccess ? (
          <WrappedComponent
            ref={ref as React.Ref<unknown>}
            hasAccess={hasAccess}
            actionScope={actionScope}
            {...props}
            {...propsIfNoAccess}
          >
            {children}
          </WrappedComponent>
        ) : null;
      }

      return (
        <WrappedComponent
          ref={ref as React.Ref<unknown>}
          hasAccess={hasAccess}
          actionScope={actionScope}
          {...props}
        >
          {children}
        </WrappedComponent>
      );
    },
  );

  const name =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';

  WithAccess.displayName = `withHasAccess(${name})`;

  return WithAccess;
}

export const HideIfNotAllowed: FC<UseWithAccess> = ({ children, ...rest }) => {
  const userData = useUser();
  const { hasAccessToResource } = useWithAccess();

  if (
    userData?.user?.userType === UserType.CustomerPortalBot &&
    rest?.ignoreForBotUsers
  ) {
    return <>{children}</>;
  }

  return hasAccessToResource(rest) ? <>{children}</> : <></>;
};

type SwitchContentBasedOnAccessProps = UseWithAccess & {
  hasAccess: ReactElement;
  doesNotHaveAccess: ReactElement;
};

export const SwitchContentBasedOnAccess: FC<SwitchContentBasedOnAccessProps> =
  ({ hasAccess, doesNotHaveAccess, ...rest }) => {
    const { hasAccessToResource } = useWithAccess();

    return hasAccessToResource(rest) ? hasAccess : doesNotHaveAccess;
  };

export const useCheckRequestScopeForProject = (project?: Project) => {
  const { hasAccessToResource, getRequestScopeForProjectResource } =
    useWithAccess();
  const projectResource = ProjectResourceOrUndefined(project);

  const hasRequestScopeAccess = useCallback(
    ({ hasData, rule }: { rule: RbacPermissionRule; hasData: boolean }) => {
      const hasAccessToTheResource = hasAccessToResource({
        rule,
        resourceData: projectResource,
      });

      const requestScope = getRequestScopeForProjectResource({
        rule,
        resourceData: projectResource,
      });

      if (hasAccessToTheResource && requestScope === 'RESTRICTED') {
        return hasData;
      }

      return hasAccessToTheResource;
    },
    [getRequestScopeForProjectResource, hasAccessToResource, projectResource],
  );

  return { hasRequestScopeAccess };
};
